import { Paper } from '@mui/material';
import React from 'react';
import { PagedRequest } from 'src/store';
import { CashStatementCsvRow, CashStatementLine } from '../store/types';
import { LoadingProgress } from 'src/common/store/types';
import { DatatableColumn, FilterDataType } from 'src/common/components/dataTable/types';
import { DateTimeFormat, formatDollars, getLocalDateTime } from 'src/common';
import { getTableRowProps, TABLE_PAPER_ELEVATION, TABLE_PAPER_SX } from 'src/common/components/dataTable/tableStyles';
import { ClientSideDataTable } from 'src/common/components/dataTable/clientSide';
import { filterOperator } from 'src/common/components/dataTable/common';

export interface Props {
  data: CashStatementLine[];
  pagedRequest: PagedRequest;
  accountLabel: string;
  showAudValues: boolean;
  handleDownload: (pagedRequest: PagedRequest, mapper: (lines: CashStatementLine[]) => CashStatementCsvRow[]) => void;
  progressCashStatements: LoadingProgress;
}

export function CashAccountsTable(props: Props): JSX.Element {
  const { data, pagedRequest, accountLabel, showAudValues, handleDownload, progressCashStatements } = props;

  const columns: DatatableColumn[] = [
    {
      name: 'date',
      label: 'DATE',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          if (!data[dataIndex]) return '';
          return getLocalDateTime(data[dataIndex].date, DateTimeFormat.Short);
        },
      },
    },
    {
      filterDataType: FilterDataType.string,
      name: 'systemTransactionType',
      label: 'TRANSACTION TYPE',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          if (!data[dataIndex]) return '';
          return data[dataIndex].systemTransactionType ?? '';
        },
      },
    },
    {
      filterDataType: FilterDataType.string,
      filterOperators: [filterOperator.contains],
      name: 'description',
      label: 'DESCRIPTION',
      options: {
        filter: true,
        sort: false,
      },
    },
    {
      filterDataType: FilterDataType.numeric,
      textAlign: 'right',
      name: 'credit',
      label: 'CREDIT',
      options: {
        filter: true,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          if (!data[dataIndex]) return '';
          const credit = data[dataIndex].credit;
          return credit ? formatDollars(Math.abs(credit)) : '';
        },
      },
    },
    {
      filterDataType: FilterDataType.numeric,
      textAlign: 'right',
      name: 'debit',
      label: 'DEBIT',
      options: {
        filter: true,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          if (!data[dataIndex]) return '';
          const debit = data[dataIndex].debit;
          return debit ? formatDollars(Math.abs(debit)) : '';
        },
      },
    },
    {
      filterDataType: FilterDataType.numeric,
      textAlign: 'right',
      name: 'balance',
      label: 'BALANCE',
      options: {
        filter: true,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          if (!data[dataIndex]) return '';
          const balance = data[dataIndex].balance;
          return balance ? formatDollars(balance) : '';
        },
      },
    },
  ];

  if (showAudValues) {
    columns.push({
      filterDataType: FilterDataType.numeric,
      textAlign: 'right',
      name: 'balanceAud',
      label: 'BALANCE (AUD)',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          if (!data[dataIndex]) return '';
          const balanceAud = data[dataIndex].balanceAud;
          return balanceAud ? formatDollars(balanceAud) : '';
        },
      },
    });
  }

  const mapCashAccountLine = (line: CashStatementLine): CashStatementCsvRow => {
    const row: CashStatementCsvRow = {
      Account: accountLabel,
      Date: line.date,
      'Transaction Type': line.systemTransactionType ?? '',
      Description: line.description ?? '',
      Credit: Math.abs(line.credit ?? 0),
      Debit: Math.abs(line.debit ?? 0),
      Balance: line.balance,
    };

    return showAudValues ? { ...row, 'Balance (AUD)': line.balanceAud ?? null } : row;
  };

  return (
    <Paper elevation={TABLE_PAPER_ELEVATION} sx={TABLE_PAPER_SX}>
      <ClientSideDataTable
        columns={columns}
        data={data}
        loadingProgress={progressCashStatements}
        enablePagination
        defaultStringFilterOperator={filterOperator.contains}
        defaultNumericFilterOperator={filterOperator.isEqual}
        options={{
          sort: false,
          filter: true,
          download: true,
          setRowProps: getTableRowProps,
          onDownload: () => {
            handleDownload(pagedRequest, (lines) => lines.map((line) => mapCashAccountLine(line)));
            return false;
          },
        }}
      />
    </Paper>
  );
}
